import React, {
  useState,
  createContext,
  useMemo,
  useCallback
} from "react";
import PropTypes from "prop-types";
import Accordion from "./Accordion";
import { IoIosSearch } from "react-icons/io";
import { MdExpand } from "react-icons/md";
import { BsArrowsCollapse } from "react-icons/bs";

export const AccordionGroupContext = createContext();

const normalize = (str) =>
  str
    ?.toString()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();

const highlightText = (node, searchTerm) => {
  if (!searchTerm) return node;
  if (typeof node === "string") {
    const parts = node.split(new RegExp(`(${searchTerm})`, "gi"));
    return parts.map((part, i) =>
      normalize(part) === normalize(searchTerm) ? (
        <mark key={i} className="bg-yellow-200">
          {part}
        </mark>
      ) : (
        part
      ),
    );
  }
  if (Array.isArray(node))
    return node.map((child) => highlightText(child, searchTerm));
  if (typeof node === "object" && node?.props?.children) {
    return React.cloneElement(node, {
      ...node.props,
      children: highlightText(node.props.children, searchTerm),
    });
  }
  return node;
};

const extractTextFromContent = (content) => {
  const texts = [];
  const extract = (node) => {
    if (!node) return;
    if (typeof node === "string" || typeof node === "number") {
      texts.push(String(node));
    } else if (Array.isArray(node)) {
      node.forEach(extract);
    } else if (typeof node === "object" && node.props?.children) {
      extract(node.props.children);
    }
  };
  extract(content);
  return texts;
};

const getMatchingIds = (items, searchTerm) => {
  const matchedIds = [];
  const searchInItem = (item, parentIds = []) => {
    const currentIds = [...parentIds, item.id];
    const titleMatch = normalize(item.title).includes(normalize(searchTerm));
    const contentMatch = extractTextFromContent(item.content).some((text) =>
      normalize(text).includes(normalize(searchTerm)),
    );

    let childMatch = false;
    if (item.children) {
      item.children.forEach((child) => {
        const childResult = searchInItem(child, currentIds);
        if (childResult.found) {
          matchedIds.push(...childResult.idsToOpen);
          childMatch = true;
        }
      });
    }

    if (titleMatch || contentMatch || childMatch) {
      return { found: true, idsToOpen: currentIds };
    }

    return { found: false, idsToOpen: [] };
  };

  items.forEach((item) => {
    const result = searchInItem(item);
    if (result.found) {
      matchedIds.push(...result.idsToOpen);
    }
  });

  return [...new Set(matchedIds)];
};

const flattenIds = (items) =>
  items.reduce((acc, item) => {
    acc.push(item.id);
    if (item.children) acc.push(...flattenIds(item.children));
    return acc;
  }, []);

const AccordionGroup = ({ items, exclusive = false, searchable = false }) => {
  const [openAccordions, setOpenAccordions] = useState([]);
  const [search, setSearch] = useState("");

  const toggleAccordion = useCallback((id) => {
    setOpenAccordions((prev) => {
      if (exclusive) return prev.includes(id) ? [] : [id];
      return prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id];
    });
  }, [exclusive]);


  const openAll = () => setOpenAccordions(flattenIds(items));
  const closeAll = () => setOpenAccordions([]);

  const matchingIds = useMemo(
    () => (search ? getMatchingIds(items, search) : []),
    [search, items],
  );

  const effectiveOpenAccordions = useMemo(() => {
    if (!exclusive && search) {
      return matchingIds;
    }

    if (!exclusive && !search) {
      return openAccordions;
    }

    return openAccordions;
  }, [exclusive, search, matchingIds, openAccordions]);

  const visibleItems = items.filter(
    (item) => !search || matchingIds.includes(item.id),
  );
  const contextValue = useMemo(
    () => ({
      openAccordions: effectiveOpenAccordions,
      toggleAccordion,
      highlightText,
      search,
    }),
    [effectiveOpenAccordions, toggleAccordion, search],
  );


  return (
    <AccordionGroupContext.Provider value={contextValue}>
      {searchable && (
        <div className="group mb-4 flex w-full flex-col">
          <div className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2">
            <IoIosSearch />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar..."
              className="peer ml-2 flex-1 bg-transparent outline-none"
            />
            {!exclusive && (
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={openAll}
                  className="text-sm text-blue-600 underline"
                >
                  <MdExpand />
                </button>
                <button
                  type="button"
                  onClick={closeAll}
                  className="text-sm text-blue-600 underline"
                >
                  <BsArrowsCollapse />
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {visibleItems.map((item) => (
        <Accordion key={item.id} item={item} search={search} />
      ))}
    </AccordionGroupContext.Provider>
  );
};

export default AccordionGroup;

AccordionGroup.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      title: PropTypes.string.isRequired,
      content: PropTypes.node,
      children: PropTypes.arrayOf(PropTypes.object),
    })
  ).isRequired,
  exclusive: PropTypes.bool,
  searchable: PropTypes.bool,
};
